import { useCallback, useRef, memo, useMemo, useEffect } from 'react'
import {
  View,
  ChatComponents,
  Thread,
  ThreadHeader,
  ChatChannelProps,
  ChatFooter,
  DateSeparator,
  PinnedMessageBanner,
  ThreadHead,
} from '@/components'
import { ChatHeader } from '@/components/chat/ChatHeader'
import { ChatMessage } from '@/components/chat/ChatMessage'
import { Chat as ChatPackage } from '@/config'
import { LayoutPanel, useIsMobile, usePinnedMessage } from '@/hooks'
import { createStyles } from '@codeleap/styles'
import { MessageList, useChannelStateContext, useChannelActionContext } from 'stream-chat-react'
import { Getstream } from '@/services'
import { waitFor } from '@codeleap/utils'
import { ChatEmptyPlaceholder } from './ChatEmptyPlaceholder'

export type ChatProps = Partial<ChatChannelProps> & {
  setChannelId: (id: string | null) => void
  channelId: ChatChannelProps['channelId']
  threadId?: string
  messageId?: string
  isChannelLoading?: boolean
}

export function Chat({ setChannelId, threadId, messageId, isChannelLoading, ...props }: ChatProps) {
  return (
    <ChatComponents.ChatChannel
      {...props}
      threadId={threadId}
      Message={ChatMessage}
      MessageTimestamp={null}
      Timestamp={null}
      DateSeparator={DateSeparator}
      MessageSystem={ChatComponents.ChatMessageSystem}
      ThreadHead={ThreadHead}
      ThreadHeader={ThreadHeader}
      listenersEnabled
      EmptyStateIndicator={ChatEmptyPlaceholder}
    >
      <ChatContent setChannelId={setChannelId} threadId={threadId} messageId={messageId} isChannelLoading={isChannelLoading} />
      <Thread />
    </ChatComponents.ChatChannel>
  )
}

const MessageListWithPadding = memo(({ channel }: { channel: any }) => {
  const { pinnedMessage } = usePinnedMessage(channel?.id, channel?.type)
  const { openThread } = Getstream.Chat.useThreadNavigation()

  const style = useMemo(() => ({
    ...styles.messageListWithPadding,
    paddingTop: pinnedMessage ? '64px' : '0px',
  }), [pinnedMessage])

  return (
    <div style={style}>
      <MessageList
        openThread={(message) => {
          openThread(message)
        }}
        // Disable default thread opening behavior, we handle it in ChatContent
        maxTimeBetweenGroupedMessages={ChatPackage.Constants.maxTimeBetweenGroupedMessages}
      />
    </div>
  )
}, (prev, next) => prev.channel?.id === next.channel?.id)

type ChatContentProps = Pick<ChatProps, 'setChannelId' | 'threadId' | 'messageId' | 'isChannelLoading'>

const ChatContent = memo(({ setChannelId, threadId, messageId, isChannelLoading }: ChatContentProps) => {
  const isMessageActionMenuOpen = ChatPackage.Store.useIsMessageActionMenuOpen()
  const { channel } = useChannelStateContext()
  const { jumpToMessage } = useChannelActionContext()
  const isMobile = useIsMobile()
  const { openThread } = Getstream.Chat.useThreadNavigation()

  useEffect(() => {
    if (threadId) {
      const thread = ChatPackage.Store.getThread()
      openThread(thread, messageId)
    }
  }, [threadId, messageId])

  useEffect(() => {
    if (messageId && !threadId && !isChannelLoading && channel) {
      const handleJumpToMessage = async () => {
        await waitFor(1)
        await jumpToMessage(messageId)
      }
      handleJumpToMessage()
    }
  }, [messageId, threadId, jumpToMessage, isChannelLoading, channel])

  const handleBack = useCallback(async () => {
    await LayoutPanel.showLeftPanel()
    setChannelId(null)
    ChatPackage.Store.setChannel(null)
  }, [setChannelId])

  const handleBackRef = useRef(handleBack)
  handleBackRef.current = handleBack

  useEffect(() => {
    if (!isMobile) return

    let touchStartX = 0
    let touchStartY = 0
    let touchEndX = 0
    let touchEndY = 0
    let hasMoved = false
    let isEdgeGesture = false

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX
      touchStartY = e.touches[0].clientY
      hasMoved = false
      isEdgeGesture = touchStartX < 50 || touchStartX > window.innerWidth - 50
    }

    const handleTouchMove = (e: TouchEvent) => {
      touchEndX = e.touches[0].clientX
      touchEndY = e.touches[0].clientY

      const deltaX = touchEndX - touchStartX
      const deltaY = touchEndY - touchStartY
      const absDeltaX = Math.abs(deltaX)
      const absDeltaY = Math.abs(deltaY)

      if (isEdgeGesture && absDeltaX > 10) {
        e.preventDefault()
        e.stopPropagation()

        if (touchStartX < 50 && absDeltaX > absDeltaY && deltaX > 20) {
          hasMoved = true
        }
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      const deltaX = touchEndX - touchStartX
      const deltaY = touchEndY - touchStartY
      const absDeltaX = Math.abs(deltaX)
      const absDeltaY = Math.abs(deltaY)

      if (isEdgeGesture && absDeltaX > 10) {
        e.preventDefault()
        e.stopPropagation()
      }

      if (hasMoved && touchStartX < 50 && absDeltaX > absDeltaY && deltaX > 100) {
        handleBackRef.current()
      }

      hasMoved = false
      isEdgeGesture = false
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd, { passive: false })

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [isMobile])

  const messageListWrapperStyle = useMemo(() => ({
    ...styles.messageListWrapper,
    ...(isMessageActionMenuOpen ? styles.messageListWrapperBlocked : styles.messageListWrapperScrollable),
  }), [isMessageActionMenuOpen])

  return (
    <View style={styles.chat}>
      <ChatHeader onBack={handleBack} />
      <View style={styles.separator} />
      <View style={messageListWrapperStyle}>
        {channel && <PinnedMessageBanner channel={channel} />}
        <MessageListWithPadding channel={channel} />
        {isMessageActionMenuOpen && (
          <View style={styles.messageListOverlay} />
        )}
      </View>
      <View style={styles.separator} />
      <ChatFooter />
    </View>
  )
})

const styles = createStyles(theme => ({
  chat: {
    ...theme.presets.column,
    ...theme.presets.flex,
    overflow: 'hidden',
    borderRadius: theme.radius.regular,
    height: '100%',

    [theme.media.down('mobile')]: {
      height: '100dvh',
      borderRadius: 0,
      position: 'relative',
      marginLeft: theme.spacing.value(0),
    },

    '& .message-highlighted': {
      animation: 'highlightMessage 2s ease-in-out',
    },

    '@keyframes highlightMessage': {
      '0%': {
        backgroundColor: 'transparent',
      },
      '15%': {
        backgroundColor: theme.baseColors.primarySolid100,
      },
      '85%': {
        backgroundColor: theme.baseColors.primarySolid100,
      },
      '100%': {
        backgroundColor: 'transparent',
      },
    },
  },

  separator: {
    ...theme.presets.fullWidth,
    height: 2,
    background: 'rgba(246, 246, 246, 1)',
  },

  messageListOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 5,
    pointerEvents: 'auto',
  },
  messageListWrapper: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  messageListWrapperBlocked: {
    overflow: 'hidden',
  },
  messageListWrapperScrollable: {
    overflow: 'auto',
  },
  messageListWithPadding: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
}))

